import React, { useEffect, useState, useCallback } from "react";
import { observer } from "mobx-react";
import { applicationStore } from "src/store/ApplicationStore";
import ProcessLoader from "src/components/animation/ProcessLoader";
import { socketInprogressServiceStatus } from "src/enums/socketInprogressServiceStatus";
import { useNavigate } from "react-router-dom";
import { autorun, toJS } from "mobx";
import { useTranslation } from "react-i18next";
import { getButtonsForNode } from "./config/errorButtonConfig";
import {
  SERVICE_REASON_MASK_RULES,
  VERIFICATION_REASON_MASK_RULES,
  getMatchingMaskRule,
} from "./config/serviceReasonConfig";
import { workflowStepIterator } from "src/services/workflowStepIterator";
import logger from "src/utils/logger";

const Processing = observer(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [, setHasRetry] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  /**
   * Masks sensitive service failure reasons.
   * Falls back to verification-level reason masking if service reason is absent.
   */
  const formatReason = useCallback((service, defaultReason) => {
    const reason = service?.reason;
    const status = service?.status;

    // 1. Check service-level reason mask rules (e.g. FORGERY, DUPLICATE FACE)
    const serviceRule = getMatchingMaskRule(reason, status, SERVICE_REASON_MASK_RULES);
    if (serviceRule) return t(serviceRule.translationKey);

    // 2. Check verification-level reason mask rules (e.g. fraud, altered document)
    const verificationRule = getMatchingMaskRule(reason, status, VERIFICATION_REASON_MASK_RULES);
    if (verificationRule) return t(verificationRule.translationKey);

    return reason || defaultReason;
  }, [t]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(
      `${window?.location?.origin}?aid=${applicationStore.aidCode}`
    );
    setShowTooltip(true);
    setTimeout(() => {
      setShowTooltip(false);
    }, 1000);
  };

  // On every mount (including page refresh) re-fetch the latest workflow state.
  // A socket message can arrive while the page is reloading and get lost; the bare
  // /workflow/next request returns the current backend state so the autorun below
  // can still route the user forward.
  useEffect(() => {
    const refreshWorkflowState = async () => {
      try {
        const response = await workflowStepIterator({
          access_token: applicationStore.access_token,
        });
        applicationStore.setWorkflowNextServiceResponse(response.data);

        if (response.data?.waiting_for_pep_step === true) {
          navigate("/processing-review", { replace: true });
          return;
        }

        const inquiry_status = response.data?.inquiry_status;
        if (inquiry_status) {
          applicationStore.setInquiryStatus(inquiry_status);
        }
      } catch (error) {
        logger.error("Processing: failed to refresh workflow state on mount", error);
      }
    };

    refreshWorkflowState();
  }, [navigate]);

  useEffect(() => {
    const disposer = autorun(() => {
      let inprogressServices = toJS(applicationStore.workflowNextServiceResponse?.inprogress_services) || [];

      if (!Array.isArray(inprogressServices) || inprogressServices.length === 0) return;

      inprogressServices = inprogressServices.filter(service => service.microservice_id !== 'pep');

      const _hasRetry = inprogressServices.some(
        (service) => service?.status === socketInprogressServiceStatus.RETRY
      );
      setHasRetry(_hasRetry);

      const hasFailed = inprogressServices.some(
        (service) => service?.status === socketInprogressServiceStatus.FAILED
      );

      const filteredErrorServices = inprogressServices.filter((service) =>
        (service?.status === socketInprogressServiceStatus.RETRY ||
          service?.status === socketInprogressServiceStatus.FAILED) &&
        service?.status !== socketInprogressServiceStatus.COMPLETED
      );

      if ((hasFailed || _hasRetry) && filteredErrorServices.length > 0) {
        applicationStore.setApplicationErrorObj({
          header: t('processing.retryHeader'),
          description: t('processing.retryDescription'),
          listItems: filteredErrorServices.map((service) => {
            const baseDescription = service?.node_settings_for_player?.label ||
              t(`nodeTypes.${service.microservice_id}`, {
                defaultValue: service.microservice_id
              });
            const defaultReason = service?.status === socketInprogressServiceStatus.FAILED
              ? t('processing.defaultFailedReason')
              : t('processing.defaultRetryReason');

            const buttons = getButtonsForNode(
              service.microservice_id,
              navigate,
              t,
              service?.node_id?.toString(),
              service?.node_settings_for_player
            );

            return {
              baseDescription,
              reason: formatReason(service, defaultReason),
              buttons: buttons.length > 0 ? buttons : undefined
            };
          })
        });
        navigate("/error-page", { replace: true });
        return;
      }

      const allCompleted = inprogressServices.every(
        (service) => service?.status === socketInprogressServiceStatus.COMPLETED
      );

      if (allCompleted) {
        const inquiry_status = applicationStore.inquiry_status;

        if (inquiry_status === 'approved') {
          setTimeout(() => {
            navigate("/success", { replace: true });
          }, 500);
        } else if (inquiry_status === 'declined') {
          applicationStore.setApplicationErrorObj({
            header: t('processing.accountFailedHeader'),
            description: t('processing.accountFailedDescription'),
            listItems: []
          });
          navigate("/error-page", { replace: true });
        }
      }
    });
    return () => disposer();
  }, [navigate, t, formatReason]);

  return (
    <div className="custom-container">
      <div className="flex flex-col items-center h-full pt-[10px]">
        <div className="pt-8">
          <div className="text-h5 font-bold mb-2 text-flow-heading">{t('processing.title')}</div>
          <div className="text-body text-Gray900">{t('processing.subtitle')}</div>
          <ProcessLoader />
        </div>
        <div className="text-left z-10">
          <ul className="list-disc list-inside text-Gray900 mt-3">
            <li>{t('processing.noAction')}</li>
            <li>
              {t('processing.checkBack')}
              {window?.top === window?.self && <span
                onClick={handleCopyLink}
                className="relative flow-inline-accent-link ms-1"
              >
                {t('processing.clickToCopy')}
                {showTooltip && (
                  <div
                    className={`absolute z-30 top-5 left-24 bg-SorrellBrown4000 text-xxs text-sm py-1 px-2 rounded-[16px] border-SorrellBrown4000 text-Gray50 shadow-lg ${showTooltip ? "animate-fadeIn" : "animate-fadeOut"
                      }`}
                  >
                    {t('processing.copied')}
                  </div>
                )}
              </span>}
            </li>
            <li>{t('processing.takesLongerPrefix')}
              <span
                onClick={() => {
                  const subject = encodeURIComponent("Verification Assistance Needed");
                  const clientId = applicationStore?.client_id;
                  const body = encodeURIComponent(
                    `Dear Plato Support Team,\n\nI am facing an issue during the
                     verification process and would like your assistance.\n\nDetails of the issue:\n-
                      Problem encountered: [Briefly describe the problem]\n- Date of occurrence: [Specify the date]\n- Device/Browser:
                       [Provide details of your device and browser]\n- Client ID: ${clientId}\n\nPlease let me know how 
                       this can be resolved.\n\nThank you for your support.\n\nBest regards,\n[Your Name]\n[Your Contact Information]`
                  );

                  const mailtoLink = `mailto:support@kyxplatform.ch?subject=${subject}&body=${body}`;

                  try {
                    window.location.href = mailtoLink;
                  } catch (error) {
                    alert("Unable to send the email. Please contact support at support@kyxplatform.ch");
                  }
                }}
                className="flow-inline-accent-link ms-1 me-1"
              >
                {t('processing.contactUs')}
              </span>
              {t('processing.takesLongerSuffix')}
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
});

export default Processing;
